/**
 * Module: lib/copilot/hooks/useConversations
 * Responsibility: react-query wrappers for /copilot/conversations list/get/delete.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authedFetch as sharedAuthedFetch } from '../authedFetch'
import type { AgentKind } from '../agents'
import type { PlanMessageDisplay, PlanStatus, PlanView } from '../plans'

const BASE = process.env.NEXT_PUBLIC_COPILOT_API_BASE ?? '/copilot-api'

// Thin wrapper so callers can pass a path relative to the copilot base and
// always get JSON Content-Type. The shared `authedFetch` handles the token +
// refresh-on-401 retry; this just prefixes the URL and the header.
async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  return sharedAuthedFetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      'Content-Type': 'application/json'
    }
  })
}

export interface ConversationSummary {
  readonly id: string
  readonly title: string | null
  /** Which agent owns the thread. Absent on responses from before agent routing. */
  readonly agent_kind?: AgentKind
  /** The thread an @-mention handed off from, if any. */
  readonly handoff_from?: string | null
  /** Something the merchant has not seen yet, e.g. a plan readout. */
  readonly unread?: boolean
  /** Status of the thread's latest CRO plan, if it has one. */
  readonly plan_status?: PlanStatus | null
  readonly created_at: string
  readonly updated_at: string
}

export function useConversationList(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['copilot', 'conversations'],
    queryFn: async (): Promise<ConversationSummary[]> => {
      const res = await authedFetch('/copilot/conversations?limit=50')
      if (!res.ok) throw new Error(`list failed: ${res.status}`)
      const body = (await res.json()) as { data: ConversationSummary[] }
      return body.data
    },
    enabled: options?.enabled ?? true
  })
}

export function useConversationDelete() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await authedFetch(`/copilot/conversations/${id}`, { method: 'DELETE' })
      if (!res.ok && res.status !== 404) throw new Error(`delete failed: ${res.status}`)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['copilot', 'conversations'] })
    }
  })
}

export interface ConversationDetail {
  readonly id: string
  readonly title: string | null
  readonly agent_kind?: AgentKind
  readonly handoff_from?: string | null
  readonly messages: ReadonlyArray<{
    readonly id: string
    readonly seq: number
    readonly role: 'user' | 'assistant' | 'tool'
    readonly content: unknown
    /** Set on messages the plan engine wrote. */
    readonly plan_id?: string | null
    readonly display?: PlanMessageDisplay | null
    /** Per-message token usage (assistant messages only, may be absent on old messages). */
    readonly usage?: {
      readonly input_tokens: number
      readonly output_tokens: number
      readonly cache_read_tokens: number
      readonly cache_creation_tokens: number
    }
  }>
  /** Cumulative token totals across the conversation (summed server-side). */
  readonly total_input_tokens?: number
  readonly total_output_tokens?: number
  /** Every CRO plan in this conversation; cards sit at the `plan_card` messages. */
  readonly plans?: readonly PlanView[]
}

export async function fetchConversation(id: string): Promise<ConversationDetail> {
  const res = await authedFetch(`/copilot/conversations/${id}`)
  if (!res.ok) throw new Error(`get failed: ${res.status}`)
  const body = (await res.json()) as { data: ConversationDetail }
  return body.data
}

// Import kept inline (instead of at file top) to avoid a circular dep between
// useConversations and useChat during module init.
import type { TranscriptEntry } from './useChat'

const AUTOMATIC_DISPLAYS: ReadonlySet<PlanMessageDisplay> = new Set<PlanMessageDisplay>([
  'plan_automatic',
  'plan_readout'
])

/** Plan-engine messages are plain text; accept a bare string or text blocks. */
function planMessageText(content: unknown): string {
  if (typeof content === 'string') return content
  const blocks = Array.isArray(content) ? (content as unknown[]) : []

  return blocks
    .filter(b => (b as { type?: string }).type === 'text')
    .map(b => (b as { text: string }).text)
    .join('\n')
}

/**
 * Plan-engine messages: a `plan_card` message becomes the card itself (its text
 * only exists for the model's replay); automatic actions and readouts keep an
 * "Automatic" tag; the confirmation block reads as a normal assistant message.
 */
function planMessageToEntry(message: ConversationDetail['messages'][number]): TranscriptEntry | null {
  if (message.display === 'plan_card') {
    return message.plan_id ? { kind: 'plan', planId: message.plan_id } : null
  }
  const text = planMessageText(message.content)
  if (!message.display || text.length === 0) return null

  return {
    kind: 'assistant',
    text,
    inFlight: false,
    ...(AUTOMATIC_DISPLAYS.has(message.display) ? { automatic: true } : {})
  }
}

/**
 * Map a persisted conversation to an in-memory transcript for replay.
 *
 * We intentionally drop persisted "tool" role messages on replay — the backend
 * stores only the tool output payload, not the tool name or running-summary
 * copy, so we can't reconstruct a meaningful ToolUseCard. The conversation
 * reads cleanly as user↔assistant; live turns continue to render tool cards
 * as tools stream in. This is a frontend-side choice; see HistoryDrawer.
 * Messages the plan engine wrote (`display` set) map through planMessageToEntry.
 */
export function messagesToTranscript(messages: ConversationDetail['messages']): TranscriptEntry[] {
  const out: TranscriptEntry[] = []
  const placedPlanIds = new Set<string>()
  for (const m of messages) {
    if (m.display) {
      const entry = planMessageToEntry(m)
      if (entry?.kind === 'plan') {
        if (placedPlanIds.has(entry.planId)) continue
        placedPlanIds.add(entry.planId)
      }
      if (entry) out.push(entry)
      continue
    }
    if (m.role === 'user') {
      const arr = Array.isArray(m.content) ? (m.content as unknown[]) : []
      const first = arr[0] as { text?: string } | undefined
      const text = first?.text ?? ''
      if (text.length > 0) out.push({ kind: 'user', text })
      continue
    }
    if (m.role === 'assistant') {
      const arr = Array.isArray(m.content) ? (m.content as unknown[]) : []
      const text = arr
        .filter(b => (b as { type?: string }).type === 'text')
        .map(b => (b as { text: string }).text)
        .join('\n')
      if (text.length > 0) {
        out.push({
          kind: 'assistant',
          text,
          inFlight: false,
          messageId: m.id,
          ...(m.usage && {
            usage: {
              input: m.usage.input_tokens,
              output: m.usage.output_tokens
            }
          })
        })
      }
      continue
    }
    // role === "tool": skip — see doc above.
  }
  return out
}

/**
 * Extract cumulative token totals from a persisted conversation.
 * Returns zeros when the server has not yet backfilled usage (old conversations).
 */
export function extractTotalTokens(detail: ConversationDetail): {
  input: number
  output: number
} {
  return {
    input: detail.total_input_tokens ?? 0,
    output: detail.total_output_tokens ?? 0
  }
}
